import React from "react";
import {
  Box,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  SuggestedRolesSkeleton,
  OtherRolesSkeleton,
  HigherRolesSkeleton,
} from "./common/InsightsSkeleton";

// Arrow component for role progression
const Arrow = ({ color, direction = "right" }) => {
  if (direction === "down") {
    return (
      <svg width="24" height="48" style={{ margin: "8px 0" }}>
        <line x1="12" y1="0" x2="12" y2="40" stroke={color} strokeWidth="4" />
        <polygon points="6,40 12,48 18,40" fill={color} />
      </svg>
    );
  }
  return (
    <svg width="48" height="24" style={{ margin: "0 8px" }}>
      <line x1="0" y1="12" x2="40" y2="12" stroke={color} strokeWidth="4" />
      <polygon points="40,6 48,12 40,18" fill={color} />
    </svg>
  );
};

const roles = [
  {
    label: "Junior",
    color: "#FFD600",
    icon: "../../assets/images/Role_Junior.svg",
  },
  {
    label: "Senior",
    color: "#2ecc40",
    icon: "../../assets/images/Role_Senior.svg",
  },
  {
    label: "Team Lead",
    color: "#2196F3",
    icon: "../../assets/images/Role_COO.svg",
  },
];

function RolesAndResponsibilities({
  isMobile,
  loading,
  suggestedRoles = [],
  otherRoles = [],
  higherRolesPromotions = [],
}) {
  const isMedium = useMediaQuery("(max-width:1200px)"); // md and below

  return (
    <>
      {/* Suggested Roles Section */}
      {loading ? (
        <SuggestedRolesSkeleton />
      ) : (
        <Grid item xs={12} sm={12} lg={6} xl={6}>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              border: "1px solid #000088",
              borderRadius: "39px",
              background: "#F3FBFF",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "poppins",
                fontWeight: 700,
                color: "#000088",
                textAlign: "center",
                mb: 2,
              }}
            >
              Suggested Roles
            </Typography>
            {suggestedRoles.length > 0 ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Roles
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  >
                    Potential Salary Increase
                  </Typography>
                </Grid>
                {suggestedRoles.map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={6}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                          src="../../assets/images/fire-icon.svg"
                          alt="fire"
                          style={{
                            width: "16px",
                            marginRight: "8px",
                          }}
                        />
                        <Typography variant="body1">{item.role}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "right",
                          fontWeight: 700,
                        }}
                      >
                        {item.estimatedSalaryIncreasePercent}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            ) : (
              <Typography align="center" color="text.secondary">
                No suggested roles found.
              </Typography>
            )}
          </Box>
        </Grid>
      )}
      {/* Other Roles with Responsibilities Section */}
      {loading ? (
        <OtherRolesSkeleton />
      ) : (
        <Grid item xs={12} sm={12} lg={6} xl={6}>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              border: "1px solid #000088",
              borderRadius: "39px",
              background: "#F3FBFF",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "poppins",
                fontWeight: 700,
                textAlign: "center",
                color: "#000088",
                mb: 2,
              }}
            >
              Other Roles with Responsibilities
            </Typography>
            {otherRoles.length > 0 ? (
              otherRoles.map((item, index) => (
                <Accordion
                  key={index}
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {item.role}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {item.responsibilities &&
                    item.responsibilities.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {item.responsibilities.map((resp, i) => (
                          <li key={i} style={{ fontSize: 13 }}>
                            {resp}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Typography variant="body2">
                        Description coming soon.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography align="center" color="text.secondary">
                No other roles found.
              </Typography>
            )}
          </Box>
        </Grid>
      )}
      {/* Higher Roles with Promotions Section */}
      {loading ? (
        <HigherRolesSkeleton />
      ) : (
        <Grid item xs={12}>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              border: "1px solid #000088",
              borderRadius: "39px",
              background: "#F3FBFF",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "poppins",
                fontWeight: 700,
                color: "#000088",
                textAlign: "center",
                mb: 2,
              }}
            >
              Higher Roles with Promotions
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "column", lg: "row" }}
              alignItems="center"
              justifyContent="center"
            >
              {(higherRolesPromotions.length > 0
                ? higherRolesPromotions
                : roles
              ).map((item, idx, arr) => (
                <React.Fragment key={item.role || item.label || idx}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mx: { xs: 0, sm: 1 },
                      my: { xs: 1, sm: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        border: `5px solid ${roles[idx]?.color || "#ccc"}`,
                        borderRadius: "50%",
                        width: 130,
                        height: 130,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={roles[idx]?.icon || ""}
                        alt={item.role || item.label || ""}
                        style={{ width: "70px", height: "70px" }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mt: 1,
                        fontFamily: "poppins",
                        textAlign: "center",
                        fontSize: "1rem !important",
                      }}
                    >
                      {item.role || item.label}
                    </Typography>
                  </Box>
                  {idx < arr.length - 1 && (
                    <Arrow
                      color={
                        idx === 0
                          ? "#FFD600"
                          : idx === 1
                          ? "#2ecc40"
                          : roles[idx + 1]?.color || "#ccc"
                      }
                      direction={isMobile || isMedium ? "down" : "right"}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Grid>
      )}
    </>
  );
}

export default RolesAndResponsibilities;
